import { Children } from 'react';
import { BlocksRenderer } from '@strapi/blocks-react-renderer';
import clsx from 'clsx';
import { IAnyType } from 'common/types/common';
import { normalizeLocalizedHref, normalizeLocalizedHtml } from 'common/utils/url';
import { useTranslation } from 'next-i18next';

import { ContentHeading } from '../ContentHeading';
import { Props } from './types';

export const ContentRichText: React.FC<Props> = ({ content, dark }) => {
  const { i18n } = useTranslation();
  const normalizeHtml = (value: string) => normalizeLocalizedHtml(value, i18n.language);

  return (
    <BlocksRenderer
      blocks={{
        code: (code: IAnyType) => {
          let text = code.plainText || '';
          if (text.includes('<table>')) {
            text = text.replace(/<table>/g, '<table class="content-table">');
          }
          return (
            <div
              dangerouslySetInnerHTML={{ __html: normalizeHtml(text) }}
              className="mb-6 mt-2 w-full overflow-x-auto"
            />
          );
        },
        paragraph: ({ children }: IAnyType) => {
          if (children?.some((item: IAnyType) => item.props.code)) {
            return children.map((item: IAnyType) => {
              if (item.props.code) {
                return (
                  <div
                    dangerouslySetInnerHTML={{ __html: normalizeHtml(item.props.text) }}
                    key={item.key}
                    className={clsx('mb-6 mt-2 w-full max-w-full', {
                      'text-white/90': dark,
                      'text-dark-blue': !dark,
                    })}
                  />
                );
              }
              if (item.props.text) {
                return (
                  <p
                    dangerouslySetInnerHTML={{ __html: normalizeHtml(item.props.text) }}
                    key={item.key}
                    className={clsx('mb-6 mt-2 max-w-full', {
                      'text-white/90': dark,
                      'text-dark-blue': !dark,
                    })}
                  />
                );
              }
              return null;
            });
          }
          if (children?.length === 1 && children[0].props.text) {
            return (
              <p
                dangerouslySetInnerHTML={{ __html: normalizeHtml(children[0].props.text) }}
                className={clsx('mb-6 mt-2 max-w-full', {
                  'text-white': dark,
                  'text-dark-blue': !dark,
                })}
              />
            );
          }
          return (
            <p
              className={clsx('mb-6 mt-2 max-w-full', {
                'text-white/90': dark,
                'text-dark-blue': !dark,
              })}>
              {Children.toArray(children)}
            </p>
          );
        },
        heading: ({ children, level }) => {
          switch (level) {
            case 1:
              return <ContentHeading type="h1">{children}</ContentHeading>;
            case 2:
              return <ContentHeading type="h2">{children}</ContentHeading>;
            case 3:
              return <ContentHeading type="h3">{children}</ContentHeading>;
            case 4:
              return <ContentHeading type="h4">{children}</ContentHeading>;
            case 5:
              return <ContentHeading type="h5">{children}</ContentHeading>;
            case 6:
              return <ContentHeading type="h6">{children}</ContentHeading>;
            default:
              return <ContentHeading type="h1">{children}</ContentHeading>;
          }
        },
        'list-item': props => {
          if (props.children && Array.isArray(props.children) && props.children.length) {
            return (
              <li className="mt-3 max-w-full">
                {props.children?.map(item => {
                  if (item.props.code) {
                    const text = item.props.text.replace(/<h3>/g, '').replace(/<\/h3>/g, '');
                    return (
                      <ContentHeading className="mb-1 mt-0" key={item.key} type="h3">
                        {text}
                      </ContentHeading>
                    );
                  }
                  return item;
                })}
              </li>
            );
          }
          return <li className="mt-3 max-w-full">{props.children}</li>;
        },
        list: props => {
          if (props.format === 'unordered') {
            return (
              <ul className="text-dark-blue list-disc pl-4 text-base" role="list">
                {Children.toArray(props.children)}
              </ul>
            );
          }
          if (props.format === 'ordered') {
            return (
              <ol className="text-dark-blue list-decimal pl-4 text-base" type="1">
                {Children.toArray(props.children)}
              </ol>
            );
          }
          return <></>;
        },
        link: props => {
          const normalizedHref = normalizeLocalizedHref(props.url, i18n.language);

          if (props.url.startsWith('http')) {
            return (
              <a
                className="text-primary"
                href={normalizedHref}
                rel="noopener noreferrer"
                target="_blank">
                {props.children}
              </a>
            );
          }
          return (
            <a className="text-primary" href={normalizedHref}>
              {props.children}
            </a>
          );
        },
      }}
      content={content as IAnyType}
      modifiers={{
        bold: ({ children }) => (
          <strong className="text-dark-blue font-semibold">{children}</strong>
        ),
        italic: ({ children }) => <span className="italic">{children}</span>,
      }}
    />
  );
};
